import {Box, Flex, Text} from 'exsportia-components'
import * as I from 'exsportia-icons'
import React, {FC, useCallback, useEffect, useState} from 'react'

import {useLocale} from '../../utils/i18n'

import {type FormQuestion} from '../../api/forms/types'
import {useCloudinaryUpload} from '../../hooks/useCloudinaryUpload'
import {DateField, DateTimeField, TimeField} from './fields/DateTimeFields'
import {FileUploadField} from './fields/FileUploadField'
import {SectionField} from './fields/SectionField'
import {CheckboxesField, DropdownField, MultipleChoiceField} from './fields/SelectionFields'
import {NumberField, ParagraphField, ShortTextField} from './fields/TextFields'
import {Icons, Theme} from './fields/types'

// eslint-disable-next-line no-underscore-dangle
export const getQuestionId = (q: FormQuestion): string => q.id || q._id || ''

const FieldIcons = I as unknown as Icons

type QuestionFieldProps = {
  question: FormQuestion
  // eslint-disable-next-line @typescript-eslint/sort-type-constituents
  value: number | string | string[] | null | undefined
  error?: string
  // eslint-disable-next-line @typescript-eslint/sort-type-constituents
  onChange: (questionId: string, value: number | string | string[]) => void
  theme: Theme
  formId?: string
}

export const QuestionField: FC<QuestionFieldProps> = ({
  question,
  value,
  error,
  onChange,
  theme,
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const {l} = useLocale()

  const {type, title, label, required, options} = question
  const qId = getQuestionId(question)

  const onUploadSuccess = useCallback((result: { secure_url: string, public_id: string }) => {
    onChange(qId, result.secure_url)
    setIsUploading(false)
    setUploadError(null)
  }, [onChange, qId])

  const onUploadError = useCallback((err: unknown) => {
    console.error('Upload error:', err)
    setIsUploading(false)
    setUploadError(typeof err === 'string' ? err : (err as any)?.statusText || l('failedToUploadFile10mb'))
  }, [l])

  const onUploadClose = useCallback(() => {
    setIsUploading(false)
  }, [])

  const {openUploadWidget, isReady} = useCloudinaryUpload(
    onUploadSuccess,
    onUploadError,
    onUploadClose
  )

  useEffect(() => {
    if (value === undefined && ['date', 'time', 'datetime'].includes(type)) {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')

      let defaultValue = ''
      if (type === 'date') {
        defaultValue = `${year}-${month}-${day}`
      } else if (type === 'time') {
        defaultValue = `${hours}:${minutes}`
      } else if (type === 'datetime') {
        defaultValue = `${year}-${month}-${day}T${hours}:${minutes}`
      }

      if (defaultValue) {
        onChange(qId, defaultValue)
      }
    }
  }, [type, value, qId, onChange])

  const renderInput = () => {
    const fieldProps = {qId, question, value, error, onChange, theme}

    switch (type) {
      case 'short_text':
        return <ShortTextField {...fieldProps} />
      case 'date':
        return <DateField {...fieldProps} />
      case 'time':
        return <TimeField {...fieldProps} />
      case 'datetime':
        return <DateTimeField {...fieldProps} />
      case 'number':
        return <NumberField {...fieldProps} />
      case 'paragraph':
        return <ParagraphField {...fieldProps} />
      case 'multiple_choice':
        return <MultipleChoiceField {...fieldProps} options={options} />
      case 'checkboxes':
        return <CheckboxesField {...fieldProps} options={options} />
      case 'dropdown':
        return (
          <DropdownField
            {...fieldProps}
            options={options}
            icons={FieldIcons}
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
          />
        )
      case 'file_upload':
        return (
          <FileUploadField
            {...fieldProps}
            icons={FieldIcons}
            isUploading={isUploading}
            isReady={isReady}
            openUploadWidget={openUploadWidget}
          />
        )
      case 'section':
        return <SectionField {...fieldProps} />
      default:
        return null
    }
  }

  if (type === 'section') {
    return (
      <Box mt='24px' mb='8px'>
        <Box mb='8px'>
          <Text settings={{fontSize: '20px', fontWeight: '700', color: theme.colors.secondary}}>
            {title || label}
          </Text>
        </Box>
        {renderInput()}
      </Box>
    )
  }

  return (
    <Box position='relative' zIndex={isDropdownOpen ? 1000 : 1}>
      <label style={{display: 'block', marginBottom: '8px'}}>
        <Flex alignItems='center' gap='4px'>
          <Text settings={{fontSize: '14px', fontWeight: '600', color: theme.colors.secondary}}>
            {title || label}
          </Text>
          {required && <Text settings={{color: '#FF4D4F', fontWeight: '700'}}>*</Text>}
        </Flex>
      </label>

      {renderInput()}

      {(error || uploadError) && (
        <Box mt='6px'>
          <Text settings={{color: '#FF4D4F', fontSize: '12px', fontWeight: '600', display: 'block'}}>
            {error || uploadError}
          </Text>
        </Box>
      )}
    </Box>
  )
}
